import { useQueryClient } from "@tanstack/react-query";
import {
  getConversation,
  getConversations,
  getConversationsMessages,
  getConversationsMessagesStatusLogs,
} from "apis/conversation";
import {
  conversationSelector,
  conversationTypeSelector,
  isAssignModeSelector,
  isFetchingConversationSelector,
  isSupervisorSelector,
  loadedInitSelector,
  selectedCompanySelector,
  selectedPhoneNumberSelector,
  userSelector,
} from "appredux/selectors";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLastMessageId,
  updateStatusMessages,
  setLastStatusLogId,
  appendBatchMessages,
  mergeMessages,
  setTyping,
  resetTyping,
  setOnlineStatus,
  updateConversationContact,
  setChat,
  setCurrentAgentId,
  setChatAIResponse,
  setConversationType,
  setPusherConnected,
  unpinConversation,
  // setChatAIResponse,
} from "appredux/chatSlice";
import { store } from "appredux/store";
import { sortById } from "utils/helper";
import { useConversationCache } from "queries/conversation";
import { getWaTags } from "apis/waTag";
import { setTags } from "appredux/configSlice";
import { usePusher } from "./usePusher";
import useTicketingEventHandler from "./useTicketingEventHandler";
import { cloneDeep, includes, isEmpty, maxBy, omit } from "lodash-es";
import { useLocation } from "react-router";
import { readStreamChatPreferenceEnabled } from "./useStreamChatPreference";
// import { chatAI } from "apis/chatAI";
import { messageTypes } from "utils/static";
import { isInhouseEnabled } from "components/apps/settings/Channels/Inhouse/helpers";
// import { store } from "appredux/store";
// import dayjs from "dayjs";

// Semua event di channel yang sama dengan alur payload yang sama → satu
// listener (eventHandler, dispatch per data_type). Tambah event baru di sini.
const eventNames = [
  "App\\Events\\WA\\WASyncChanged",
  "inhouse.guest.promoted",
  "inhouse.guest.migrated",
];

const CONVERSATIONS_LIST_ROUTE = /^\/conversations\/[^/]+\/list$/;

// Timeout pendek khusus fetch detail conversation di jalur realtime. Jauh lebih
// kecil dari timeout global (100s) supaya kegagalan/hang cepat terdeteksi dan
// fallback rekonsiliasi (refetch list) bisa segera jalan — bukan menggantung 100s.
const REALTIME_DETAIL_TIMEOUT_MS = 20000;

// Reconnect Pusher yang lebih singkat dari ini dianggap flicker — tidak memicu
// catch-up (mustahil ada event tertinggal pada putus <3s). Mengurangi fetch kosong
// saat idle. Reconnect lebih lama tetap catch-up (memulihkan event yang hilang).
const RECONNECT_CATCHUP_THRESHOLD_MS = 3000;

// const sendMessageFromAI = async ({ currentConversation, lastMessage }) => {
//   const messages = store.getState().chat.messages || [];
//   const maxChatHistoryForChatAI = import.meta.env
//     .VITE_APP_MAX_CHAT_HISTORY_FOR_CHATAI;

//   // Ambil history percakapan terakhir
//   const historyMessages = messages
//     .filter(
//       (msg) =>
//         dayjs(msg.message_time).isBefore(dayjs(lastMessage.message_time)) &&
//         msg.type_id !== messageTypes.SYSTEM
//     )
//     .sort((a, b) => dayjs(a.message_time).diff(dayjs(b.message_time)))
//     .slice(-maxChatHistoryForChatAI);

//   // Buat format prompt dengan history + pesan terakhir
//   let formattedPrompt = historyMessages
//     .map((msg) => `${msg.is_out ? "Agent" : "User"}: ${msg.message_text}`)
//     .join("\n");

//   // Tambahkan pesan terakhir
//   formattedPrompt += `\nUser: ${lastMessage.message_text}`;

//   const phoneNumberId = currentConversation?.phone_number_id;
//   const chatAIResult = await chatAI({
//     message: formattedPrompt,
//     phone_number_id: phoneNumberId,
//     cache: "1",
//     debug: "1",
//   });
//   let message = null;

//   if (chatAIResult?.status === 200 && !_.isEmpty(chatAIResult.data?.response)) {
//     message = chatAIResult.data.response;
//   }
//   return {
//     message,
//     question: formattedPrompt,
//     phoneNumberId,
//     messageId: lastMessage.id,
//     conversationId: currentConversation.id,
//   };
// };

const useMessageChannel = ({
  playNotificationSound,
  meetingReminderHandler,
  meetingFinishedHandler,
  meetingScheduleNotificationHandler,
}) => {
  // Single-flight + trailing rerun: jika sync sedang berjalan dan datang event
  // lagi, cukup tandai `dirty` supaya dijalankan tepat sekali lagi setelah yang
  // berjalan selesai. Menjamin tidak ada event Pusher yang ter-drop saat burst.
  const messageFlight = useRef({ running: false, dirty: false });
  const statusFlight = useRef({ running: false, dirty: false });
  const closedFlight = useRef({ running: false, dirty: false });
  // Ref ke fungsi sync terbaru, dipakai jalur retry on-error agar tetap lewat
  // single-flight (hindari ketergantungan sirkular pada useCallback).
  const runMessageSyncRef = useRef(null);
  const runStatusSyncRef = useRef(null);
  const messageRetryRef = useRef(null);
  const statusRetryRef = useRef(null);
  // false setelah unmount (mis. redirect ke /user/login): request yang masih
  // in-flight saat unmount tidak boleh memasang retry-timer baru, kalau tidak
  // timer yatim itu terus nembak endpoint dari closure lama.
  const isMountedRef = useRef(true);
  const eventBuffer = useRef([]);
  // Mirror isFetchingConversation untuk dibaca di dalam closure setBuffer.
  const isFetchingConvRef = useRef(false);
  // Timestamp saat koneksi Pusher terakhir lepas dari "connected" (untuk gate
  // durasi disconnect → skip catch-up pada flicker singkat).
  const disconnectedAtRef = useRef(0);
  const { pathname } = useLocation();
  const isSupervisor = useSelector(isSupervisorSelector);
  const selectedPhoneNumber = useSelector(selectedPhoneNumberSelector);

  const isFetchingConversation = useSelector(isFetchingConversationSelector);
  const loadedInit = useSelector(loadedInitSelector);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const currentConversation = useSelector(conversationSelector);

  // Cursor (lastMessageId / lastStatusLogId) sengaja dibaca dari store saat
  // eksekusi handler (lihat messageHandler/statusUpdatedHandler), bukan via
  // selector closure, agar single-flight rerun selalu memakai nilai terbaru.
  const { id: agentId } = useSelector(userSelector);
  const selectedCompany = useSelector(selectedCompanySelector);
  const user = useSelector(userSelector);
  const channelName = selectedCompany?.website_id
    ? `private-wa-sync-changed-${selectedCompany?.website_id}`
    : null;
  const { channelRef, pusherRef } = usePusher(channelName);
  const { ticketingHandler } = useTicketingEventHandler();
  const conversationType = useSelector(conversationTypeSelector);
  const isAssignMode = useSelector(isAssignModeSelector);
  const queryKey = useMemo(() => {
    return !isAssignMode
      ? ["conversations", "list"]
      : ["conversations", "list", { inbox_filter: conversationType }];
  }, [conversationType, isAssignMode]);
  // const isCanSendMessage = isAssignMode && conversationType === "my_inbox";

  const shouldSupervisorStreamAssign =
    readStreamChatPreferenceEnabled() &&
    isSupervisor &&
    isAssignMode &&
    CONVERSATIONS_LIST_ROUTE.test(pathname);

  const {
    updateStatusMessagesConversationCache,
    removeConversationCache,
    appendMessagesToConversationCache,
    updateConversationContactCache,
  } = useConversationCache();

  const appendToCacheConversation = useCallback(
    async (conversation, maxMexssageId) => {
      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old) => {
        let newData = cloneDeep(old);
        if (!isEmpty(newData?.data) && Array.isArray(newData?.data)) {
          const findData = newData.data.find(
            (row) => row.id === conversation.id,
          );

          if (!findData) {
            newData.data.push(conversation);
          }
          newData.data = sortById(newData.data);
        } else {
          // jika data awal kosong
          if (isEmpty(newData?.data) && isAssignMode) {
            const last_message_id = maxMexssageId
              ? maxMexssageId
              : maxBy(conversation.message, "id")?.id;

            newData = {
              data: [{ ...conversation }],
              last_message_id: last_message_id,
            };
          }
        }

        return newData;
      });
    },
    [queryClient, isAssignMode, queryKey],
  );

  // Ambil detail beberapa conversation secara tahan-gagal:
  // - timeout pendek supaya tidak menggantung 100s,
  // - allSettled supaya satu yang gagal tidak menggagalkan yang lain,
  // - bila ADA yang gagal, rekonsiliasi via refetch list (sumber otoritatif)
  //   agar conversation baru tetap masuk inbox meski detail-nya gagal di-fetch.
  // Mengembalikan array objek conversation yang berhasil di-fetch.
  const fetchConversationsDetail = useCallback(
    async (ids = []) => {
      if (isEmpty(ids)) return [];
      const settled = await Promise.allSettled(
        ids.map((id) =>
          getConversation(id, undefined, {
            timeout: REALTIME_DETAIL_TIMEOUT_MS,
          }),
        ),
      );
      const conversations = [];
      let anyFailed = false;
      settled.forEach((res) => {
        if (res.status === "fulfilled" && !isEmpty(res.value?.data?.data)) {
          conversations.push(res.value.data.data);
        } else if (res.status === "rejected") {
          anyFailed = true;
        }
      });
      if (anyFailed) {
        // Background refetch query aktif (tidak nge-blank list, lihat gate spinner)
        queryClient
          .invalidateQueries({ queryKey, refetchType: "active" })
          .catch(() => {});
      }
      return conversations;
    },
    [queryClient, queryKey],
  );

  const conversationClosedHandler = useCallback(async () => {
    try {
      const result = await getConversations(
        {
          include_conversation_details: 0,
          include_messages: 0,
          inbox_filter: isAssignMode ? conversationType : null,
        },
        // timeout pendek: ini cuma rekonsiliasi cache, jangan menggantung 100s
        { timeout: REALTIME_DETAIL_TIMEOUT_MS },
      );
      if (result.data?.success) {
        const resultData = result.data?.data;
        removeConversationCache(resultData, queryKey);
      }
      return true;
    } catch (error) {
      // Jangan lempar ke atas (mencegah unhandled rejection → spam Sentry).
      // Rekonsiliasi akan terjadi lagi pada event/catch-up berikutnya.
      console.error("conversationClosedHandler error:", error);
      return false;
    }
  }, [removeConversationCache, isAssignMode, conversationType, queryKey]);

  // Single-flight + trailing rerun untuk reconciliation conversation_closed
  // (fetch list penuh). Mencegah banyak close-event beruntun menembakkan
  // banyak fetch-list bersamaan ke BE.
  const runClosedSync = useCallback(async () => {
    if (closedFlight.current.running) {
      closedFlight.current.dirty = true;
      return;
    }
    closedFlight.current.running = true;
    try {
      do {
        closedFlight.current.dirty = false;
        const ok = await conversationClosedHandler();
        if (!ok) break;
      } while (closedFlight.current.dirty);
    } finally {
      closedFlight.current.running = false;
    }
  }, [conversationClosedHandler]);

  // Jalur broadcast: BE mengirim conversation_ids yang ditutup → hapus langsung
  // dari cache tab aktif, TANPA fetch reconcile. Parity dgn handler lama: tidak
  // menyentuh open-chat (close sendiri sudah di-reset oleh useCloseConversation).
  const applyBroadcastClosed = useCallback(
    (payload) => {
      const ids = Array.isArray(payload.conversation_ids)
        ? payload.conversation_ids
        : [];
      if (isEmpty(ids)) return;
      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old;
        return {
          ...old,
          data: old.data.filter((row) => !ids.includes(row.id)),
        };
      });
      // Pin hidup DI LUAR query cache, jadi menghapus dari cache saja malah
      // membuat pin lolos saringan `!existingIds.has(pin.id)` di halaman
      // conversations dan conversation-nya muncul lagi sebagai baris hantu yang
      // tak bisa hilang (pin closed tak pernah terekonsiliasi oleh fetch).
      // Perlu di sini karena close bisa datang dari luar sesi ini (supervisor
      // menutup, auto-close BE) — bukan hanya lewat tombol close di tab ini,
      // yang sudah ditangani useCloseConversation.
      ids.forEach((conversationId) => {
        dispatch(unpinConversation(conversationId));
      });
    },
    [queryClient, queryKey, dispatch],
  );

  // Proses status logs ke redux + cache. Dipakai oleh jalur fetch (statusUpdatedHandler)
  // maupun jalur broadcast (applyBroadcastStatus). Cursor hanya maju (monotonic).
  const processIncomingStatusLogs = useCallback(
    (logs, { lastStatusLogId } = {}) => {
      if (isEmpty(logs) || !Array.isArray(logs)) return;
      // Update status_id ke redux messages
      dispatch(updateStatusMessages(logs));
      // Advance cursor (hanya maju) untuk cegah regresi saat broadcast & catch-up overlap
      const next = lastStatusLogId || maxBy(logs, "id")?.id;
      const current = store.getState().chat.lastStatusLogId;
      if (next && next > current) {
        dispatch(setLastStatusLogId(next));
      }
      updateStatusMessagesConversationCache(logs);
    },
    [dispatch, updateStatusMessagesConversationCache],
  );

  const statusUpdatedHandler = useCallback(async () => {
    try {
      if (statusRetryRef.current) {
        clearTimeout(statusRetryRef.current);
        statusRetryRef.current = null;
      }

      // Baca cursor dari store saat eksekusi (bukan closure) supaya rerun
      // trailing pada single-flight memakai cursor terbaru.
      const lastId = store.getState().chat.lastStatusLogId;
      const result = await getConversationsMessagesStatusLogs({
        last_id: lastId,
      });
      if (result.data?.success) {
        processIncomingStatusLogs(result.data?.data);
      }
      return true;
    } catch (error) {
      console.error("statusUpdatedHandler error:", error);
      if (!isMountedRef.current) return false;
      statusRetryRef.current = setTimeout(() => {
        statusRetryRef.current = null;
        runStatusSyncRef.current?.();
      }, 5000);
      return false;
    }
  }, [processIncomingStatusLogs]);

  // Jalur broadcast: BE sudah membawa status_logs inline → langsung proses, TANPA fetch.
  const applyBroadcastStatus = useCallback(
    (payload) => {
      processIncomingStatusLogs(payload.status_logs, {
        lastStatusLogId: payload.last_status_log_id,
      });
    },
    [processIncomingStatusLogs],
  );

  const focusConversationHandler = useCallback(
    async (conversation_id) => {
      const conversationCache = queryClient.getQueryData(queryKey);
      if (Array.isArray(conversationCache?.data)) {
        const findConversation = conversationCache.data.find(
          (row) => row.id === conversation_id,
        );
        if (!isEmpty(findConversation)) {
          dispatch(
            setChat({
              conversation: omit(findConversation, "messages"),
              messages: findConversation.messages,
            }),
          );
        }
      }
    },
    [queryClient, queryKey, dispatch],
  );

  // Proses array messages ke redux + cache. Dipakai jalur fetch (messageHandler)
  // maupun jalur broadcast (applyBroadcastMessages). Urutan crash-safe:
  // append dulu → advance cursor → terakhir fetch detail conv baru.
  const processIncomingMessages = useCallback(
    async (
      messages,
      {
        lastMessageId,
        conversations: embeddedConversations,
        genericFilter,
      } = {},
    ) => {
      if (isEmpty(messages) || !Array.isArray(messages)) return;

      // Keanggotaan "my_inbox". Untuk jalur broadcast (genericFilter) field `filter`
      // dikirim se-company sehingga tidak bisa menentukan my_inbox per-agent → pakai
      // agent assignment (current_agent_id || agent_id) === user yang login + filter
      // "assigned". Untuk jalur fetch (endpoint terotentikasi) `filter` sudah per-agent.
      const isMyInbox = (row) => {
        const assignedAgentId = row.current_agent_id || row.agent_id;
        return genericFilter
          ? assignedAgentId === user.id &&
              Array.isArray(row.filter) &&
              row.filter.includes("assigned")
          : Array.isArray(row.filter) && row.filter.includes("my_inbox");
      };

      // Untuk menambahkan message ke conversations selected (yang ada diredux saat ini)
      const messageInCurrentConversations = [];
      const requestIds = [];
      const assigned_conversation_id = [];
      // Tandai bila batch ini memuat pesan MASUK untuk user yang sedang login.
      let hasIncomingForMe = false;

      // Ambil cache
      const cache = queryClient.getQueryData(queryKey);
      const conversations = cache?.data || [];

      // ── Deteksi pengalihan manual (takeover lewat kirim pesan) ─────────────
      // Pada message: agent_id = pemilik BARU (yang terakhir kirim → otomatis
      // ambil alih, tanpa hit API), current_agent_id = pemilik LAMA (0 = belum
      // di-assign). Sengaja DISEMPITKAN ke pesan keluar dari agent
      // (is_out===1, bukan bot) & pemilik berubah, supaya pesan customer/bot
      // sama sekali tidak terpengaruh. Pakai pesan ber-id terbesar per conv.
      const reassignByConv = new Map();
      if (isAssignMode) {
        messages.forEach((row) => {
          const newAgent = row.agent_id;
          const oldAgent = row.current_agent_id;
          if (
            row.is_out === 1 &&
            row.is_bot !== 1 &&
            newAgent > 0 &&
            newAgent !== oldAgent
          ) {
            const prev = reassignByConv.get(row.conversation_id);
            if (!prev || row.id > prev.id) {
              reassignByConv.set(row.conversation_id, {
                id: row.id,
                oldAgent,
                newAgent,
              });
            }
          }
        });
      }

      // Looping messages (terakhir sampai terbaru)
      messages.forEach((row) => {
        //jika ternyata ada message yang conversation id nya sama dengan conversatiion selected(yg diredux)
        if (row.conversation_id === currentConversation?.id) {
          messageInCurrentConversations.push(row);
        }
        //  message dicari di cache conversation saat ini
        const findInConversation = conversations.find(
          (conv) => conv.id === row.conversation_id,
        );
        // Notif suara hanya untuk pesan MASUK dari customer (is_out === 0, dan
        // bukan pesan sistem spt alihan/unassign) DI conversation milik user
        // yang sedang login. Pesan keluar (kita yang kirim) maupun pesan untuk
        // agent lain tidak memicu bunyi.
        if (
          isMyInbox(row) &&
          row.is_out === 0 &&
          !messageTypes.SYSTEM.includes(row.type_id) &&
          !reassignByConv.has(row.conversation_id)
        ) {
          hasIncomingForMe = true;
        }
        if (
          shouldSupervisorStreamAssign &&
          row.filter.includes("assigned") &&
          selectedPhoneNumber?.id === row.phone_number_id
        ) {
          if (conversationType !== "assigned") {
            dispatch(setConversationType("assigned"));
          }
          assigned_conversation_id.push(row.conversation_id);
        }

        //jika tidak ditemukan di cache saat ini(berarti conversation baru), dicatat id untuk nanti di request api
        if (!findInConversation && !includes(requestIds, row.conversation_id)) {
          if (isAssignMode) {
            // Tentukan apakah conversation baru ini masuk tab yang sedang dibuka
            let shouldAdd;
            if (conversationType === "all") {
              shouldAdd = true;
            } else if (conversationType === "my_inbox") {
              // Tambah juga bila pesan ini mengalihkan kepemilikan ke saya
              // (takeover). isMyInbox lama membaca current_agent_id (pemilik
              // lama) → tak akan true untuk owner baru, jadi perlu cek eksplisit.
              const ra = reassignByConv.get(row.conversation_id);
              shouldAdd = isMyInbox(row) || ra?.newAgent === user.id;
            } else {
              shouldAdd =
                Array.isArray(row.filter) &&
                row.filter.includes(conversationType);
            }
            if (shouldAdd) {
              requestIds.push(row.conversation_id);
            }
          } else {
            requestIds.push(row.conversation_id);
          }
        }
      });

      //gabungkan message ke dalam conversation selected (redux)
      if (!isEmpty(messageInCurrentConversations)) {
        dispatch(appendBatchMessages(messageInCurrentConversations));
      }

      // Simpan message ke cache conversation
      await appendMessagesToConversationCache(messages, queryKey);

      // Bunyi HANYA bila ada pesan masuk untuk user yang login (lihat penanda
      // hasIncomingForMe di loop atas). Tidak lagi bergantung isAssignMode —
      // pesan keluar & pesan untuk agent lain sudah pasti tidak ikut.
      if (hasIncomingForMe) {
        playNotificationSound();
      }

      // Advance cursor (hanya maju) SETELAH append sukses → crash-safe + anti-regresi
      const next = lastMessageId || maxBy(messages, "id")?.id;
      const current = store.getState().chat.lastMessageId;
      if (next && next > current) {
        dispatch(setLastMessageId(next));
      }

      // Conversation baru: pakai object embedded dari broadcast bila ada,
      // sisanya fetch detail (tahan-gagal: allSettled + timeout + fallback invalidate).
      const embedded = Array.isArray(embeddedConversations)
        ? embeddedConversations
        : [];
      const embeddedIds = [];
      embedded.forEach((conversation) => {
        // Forward-safe: di tab my_inbox jalur broadcast, hanya append conversation
        // yang assign-nya ke user yang login (filter broadcast generik se-company).
        if (genericFilter && conversationType === "my_inbox") {
          const assignedAgentId =
            conversation.current_agent_id || conversation.agent_id;
          if (assignedAgentId !== user.id) return;
        }
        // __historyLoaded: penanda FE-only (TIDAK dikirim ke BE). Embedded
        // broadcast hanya membawa messages minim → tandai false supaya
        // useOpenConversation memuat history penuh saat conversation dibuka.
        // Path lain (fetchConversationsDetail, list awal) sudah full → tak
        // perlu flag, dianggap lengkap.
        appendToCacheConversation(
          { ...conversation, __historyLoaded: false },
          next,
        );
        embeddedIds.push(conversation.id);
      });
      const idsToFetch = requestIds.filter((id) => !embeddedIds.includes(id));
      const newConversations = await fetchConversationsDetail(idsToFetch);
      newConversations.forEach((conversation) => {
        appendToCacheConversation(conversation, next);
      });

      if (assigned_conversation_id.length > 0) {
        focusConversationHandler(assigned_conversation_id[0]);
      }

      // ── Terapkan pengalihan untuk conv yang SUDAH ada di cache ─────────────
      // (Conv yang BELUM ada & owner barunya = saya pada my_inbox sudah ditangani
      // jalur requestIds → fetch+append di atas; di sini tidak di-fetch ulang.)
      if (isAssignMode && reassignByConv.size > 0) {
        const removeIds = [];
        reassignByConv.forEach(({ newAgent }, convId) => {
          const inCache = conversations.some((c) => c.id === convId);
          if (conversationType === "unassigned") {
            // Sudah punya owner → keluar dari tab unassigned.
            if (inCache) removeIds.push(convId);
          } else if (conversationType === "my_inbox") {
            // Dialihkan ke orang lain → hilang dari my_inbox saya.
            if (newAgent !== user.id && inCache) removeIds.push(convId);
          } else if (
            conversationType === "assigned" ||
            conversationType === "all"
          ) {
            // Cukup ganti pemilik tampilan ke agent baru (sudah ada di cache).
            if (inCache) {
              queryClient.setQueryData(queryKey, (old) => {
                if (!old?.data || !Array.isArray(old.data)) return old;
                return {
                  ...old,
                  data: old.data.map((row) =>
                    row.id === convId
                      ? { ...row, current_agent_id: newAgent }
                      : row,
                  ),
                };
              });
              if (currentConversation?.id === convId) {
                dispatch(setCurrentAgentId(newAgent));
              }
            }
          }
        });
        if (removeIds.length > 0) {
          queryClient.setQueryData(queryKey, (old) => {
            if (!old?.data || !Array.isArray(old.data)) return old;
            return {
              ...old,
              data: old.data.filter((row) => !removeIds.includes(row.id)),
            };
          });
          if (
            currentConversation?.id &&
            removeIds.includes(currentConversation.id)
          ) {
            dispatch(setChat({ conversation: null, messages: [] }));
          }
        }
      }

      // Re-sync panel chat yang sedang terbuka dari cache: kalau cache punya pesan
      // yang belum ada di redux (mis. history conversation baru yang baru landing),
      // merge ke redux supaya history tampil tanpa perlu refresh. Merge (union by
      // id) menjaga pesan optimistic yang sedang dikirim.
      //
      // PENTING: baca conversation yang terbuka dari store HIDUP (store.getState),
      // BUKAN dari closure `currentConversation`. Saat stream chat ON, fokus bisa
      // berpindah otomatis (auto-focus) di tengah fungsi async ini sehingga nilai
      // closure jadi basi → tanpa guard ini, mergeMessages bisa menumpahkan pesan
      // conversation lain ke conversation yang sekarang justru sedang dibuka.
      // (Pola anti-race yang sama dipakai di useOpenConversation/queries.)
      const openConversationId = store.getState().chat.conversation?.id;
      if (openConversationId) {
        const openConv = queryClient
          .getQueryData(queryKey)
          ?.data?.find((c) => c.id === openConversationId);
        if (!isEmpty(openConv?.messages)) {
          const reduxIds = new Set(
            (store.getState().chat.messages || []).map((m) => m.id),
          );
          const hasMissing = openConv.messages.some((m) => !reduxIds.has(m.id));
          if (hasMissing) {
            dispatch(mergeMessages(openConv.messages));
          }
        }
      }
    },
    [
      queryClient,
      queryKey,
      appendMessagesToConversationCache,
      isAssignMode,
      dispatch,
      currentConversation,
      conversationType,
      playNotificationSound,
      appendToCacheConversation,
      fetchConversationsDetail,
      focusConversationHandler,
      shouldSupervisorStreamAssign,
      selectedPhoneNumber,
      user.id,
    ],
  );

  const messageHandler = useCallback(async () => {
    try {
      if (messageRetryRef.current) {
        clearTimeout(messageRetryRef.current);
        messageRetryRef.current = null;
      }

      // Baca cursor dari store saat eksekusi (bukan closure) supaya rerun
      // trailing pada single-flight memakai cursor terbaru.
      const lastId = store.getState().chat.lastMessageId;
      // Cursor belum di-seed dari daftar conversation (masih 0). Jangan tarik
      // "semua" pesan via last_id=0 — daftar (inbox_filter) sudah membawa seluruh
      // conversation berlangsung + last_message_id (watermark). Catch-up incremental
      // jalan SETELAH cursor terisi (di-seed list / broadcast live), dipicu effect
      // seed di bawah. Mencegah badai fetch detail conversation saat refresh.
      if (!lastId) return true;
      //mendapatkan message terbaru dari last message sampai yang terbaru
      const result = await getConversationsMessages(lastId);

      if (result.data?.success) {
        await processIncomingMessages(result.data?.data || []);
      }
      return true;
    } catch (error) {
      console.error("messageHandler error:", error);
      // Jangan lanjutkan trailing-rerun (hindari busy-spin saat outage);
      // serahkan ke retry-timer dengan backoff 5s.
      if (!isMountedRef.current) return false;
      messageRetryRef.current = setTimeout(() => {
        messageRetryRef.current = null;
        runMessageSyncRef.current?.();
      }, 5000);
      return false;
    }
  }, [processIncomingMessages]);

  // Jalur broadcast: BE sudah membawa messages inline → langsung proses, TANPA fetch.
  const applyBroadcastMessages = useCallback(
    (payload) =>
      processIncomingMessages(payload.messages, {
        lastMessageId: payload.last_message_id,
        conversations: payload.conversations,
        // Broadcast se-company → filter generik, my_inbox dinilai via current_agent_id.
        genericFilter: true,
      }),
    [processIncomingMessages],
  );

  // Single-flight + trailing rerun untuk message sync. Event yang datang saat
  // sync berjalan tidak di-drop, tapi memicu tepat satu sync tambahan setelah
  // yang berjalan selesai (cursor sudah maju di store).
  const runMessageSync = useCallback(async () => {
    if (messageFlight.current.running) {
      messageFlight.current.dirty = true;
      return;
    }
    messageFlight.current.running = true;
    try {
      do {
        messageFlight.current.dirty = false;
        const ok = await messageHandler();
        if (!ok) break; // gagal → biar retry-timer yang lanjut, jangan spin
      } while (messageFlight.current.dirty);
    } finally {
      messageFlight.current.running = false;
    }
  }, [messageHandler]);

  const runStatusSync = useCallback(async () => {
    if (statusFlight.current.running) {
      statusFlight.current.dirty = true;
      return;
    }
    statusFlight.current.running = true;
    try {
      do {
        statusFlight.current.dirty = false;
        const ok = await statusUpdatedHandler();
        if (!ok) break; // gagal → biar retry-timer yang lanjut, jangan spin
      } while (statusFlight.current.dirty);
    } finally {
      statusFlight.current.running = false;
    }
  }, [statusUpdatedHandler]);

  // Simpan referensi terbaru agar jalur retry on-error tetap lewat single-flight.
  runMessageSyncRef.current = runMessageSync;
  runStatusSyncRef.current = runStatusSync;

  // Matikan retry-timer saat unmount. Listener Pusher/window sudah punya cleanup
  // sendiri, tapi timer retry tidak → tanpa ini, setelah redirect ke login
  // (sesi habis) statusLogs?last_id=… terus di-retry tiap 5s dan 401 selamanya.
  // isMountedRef di-set true di sini (bukan cuma initial value) supaya aman
  // dengan double mount → unmount → mount StrictMode.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(messageRetryRef.current);
      clearTimeout(statusRetryRef.current);
      messageRetryRef.current = null;
      statusRetryRef.current = null;
    };
  }, []);

  /**
   * Update the initial state for an online agent.
   *
   * @param {Object} payload The payload object containing the following properties:
   *   - agent_id: number - The ID of the agent.
   *   - data_type: string - The type of data being updated, should be 'online'.
   *   - online: number - Indicates whether the agent is online or offline.
   *                      1 for online, 0 for offline.
   * @return {void}
   */
  const updateOnlineStatus = useCallback(
    async (payload) => {
      //   {
      //     "agent_id": 270324,
      //     "online": 0,
      //     "data_type": "online"
      // }
      if (payload.agent_id === user.id) {
        dispatch(setOnlineStatus(payload.online));
      }
      const queryCache = queryClient.getQueryCache().findAll();
      queryCache.forEach((query) => {
        if (query.queryKey.includes("agents")) {
          const cacheData = query.state.data;
          // Cek apakah agent_id di cache sama dengan agent_id dari event
          if (
            cacheData &&
            cacheData.some((agent) => agent.id === payload.agent_id)
          ) {
            // Jika cocok, update cache menggunakan setQueryData
            queryClient.setQueryData(query.queryKey, (old) => {
              let newData = cloneDeep(old);
              newData = newData?.map((agent) => {
                if (agent.id === payload.agent_id) {
                  agent.online = payload.online === 1;
                }
                return agent;
              });
              return newData;
            });
          }
        }
        if (query.queryKey.includes("divisions")) {
          const cacheData = query.state.data;
          if (!isEmpty(cacheData) && !isEmpty(cacheData.data)) {
            queryClient.setQueryData(query.queryKey, (old) => {
              let newData = cloneDeep(old);
              newData.data = newData.data.map((division) => {
                if (
                  division.onlineAgents.some((id) => id === payload.agent_id)
                ) {
                  return {
                    ...division,
                    onlineAgents: division.onlineAgents.filter(
                      (id) => id !== payload.agent_id,
                    ),
                  };
                } else {
                  return {
                    ...division,
                    onlineAgents: [...division.onlineAgents, payload.agent_id],
                  };
                }
              });
              return newData;
            });
          }
        }
      });
    },
    [dispatch, user.id, queryClient],
  );

  const setTypingHandler = useCallback(
    async (payload) => {
      if (
        payload?.agent_id !== agentId &&
        currentConversation?.id === payload?.conversation_id
      ) {
        dispatch(setTyping(payload));
        // Direset dalam 5 detik
        setTimeout(() => {
          dispatch(resetTyping());
        }, 5000);
      }
    },
    [agentId, currentConversation?.id, dispatch],
  );

  const setTagsHandler = useCallback(async () => {
    try {
      const result = await getWaTags({ per_page: 999 }); // di set 999 untuk mengganti semua tag yang ada di redux
      if (result.data.success) {
        dispatch(setTags(result.data.data?.data));
      }
    } catch (error) {
      console.error("setTagsHandler error:", error);
    }
  }, [dispatch]);

  const updateContactTags = useCallback(
    async (result) => {
      const payload = {
        tag_ids: result?.tag_ids || [],
        id: result?.contact_id,
      };
      dispatch(updateConversationContact(payload));
      updateConversationContactCache(payload);
    },
    [dispatch, updateConversationContactCache],
  );

  const assignHandler = useCallback(
    async (payload) => {
      // payload = {
      //   coversation_id:104,
      //   data_type:"assign",
      //   assigned_agent_id:270324,
      // }

      // case siapapun yang nge assign, (bisa di filter assigned, my_inbox, unassigned) sudah ditangani oleh useQuery

      // kalau user di unassigned bakal hilang dari cache
      // kalau user di my_inbox bakal di cek apakah assigned_agent_id nya sama dengan current user, jika ada cek | engga hapus
      // kalau user di assigned bakal di cek apakah udh ada, jika blm get detail

      // cek jika spv, dan onstream hidup, pindahkan conversationType ke assigned, dan buat fokus ke conversation_id

      if (
        shouldSupervisorStreamAssign &&
        payload.assigned_agent_id !== user.id
      ) {
        // pindahkan conversation ke assigned
        dispatch(setConversationType("assigned"));
      }

      const conversations = queryClient.getQueryData(queryKey);
      const findConversation = conversations?.data?.find(
        (row) => row.id === payload.conversation_id,
      );
      const detailIds = [];
      if (conversationType === "unassigned") {
        if (!findConversation) return;
        queryClient.setQueryData(queryKey, (old) => {
          let newData = cloneDeep(old);
          newData.data = newData.data.filter(
            (row) => row.id !== payload.conversation_id,
          );
          return newData;
        });
        if (currentConversation?.id === payload.conversation_id) {
          dispatch(setChat({ conversation: null, messages: [] }));
        }
      } else if (conversationType === "my_inbox") {
        //jika my_inbox, jika assign id nya bukan dia tapi conv_id nya ada dicache => hapus
        if (user.id !== payload.assigned_agent_id && findConversation) {
          queryClient.setQueryData(queryKey, (old) => {
            let newData = cloneDeep(old);
            newData.data = newData.data.filter(
              (row) => row.id !== payload.conversation_id,
            );
            return newData;
          });
          if (currentConversation?.id === payload.conversation_id) {
            dispatch(setChat({ conversation: null, messages: [] }));
          }
        }
        //jika my_inbox, jika assign id nya sama, jika blm ada conv_id nya di cache => get detail append
        if (user.id === payload.assigned_agent_id && !findConversation) {
          detailIds.push(payload.conversation_id);
          playNotificationSound();
        }
      } else if (conversationType === "assigned") {
        if (currentConversation?.id === payload.conversation_id) {
          dispatch(setCurrentAgentId(payload.assigned_agent_id));
        }
        if (!findConversation) {
          // jika di assigned, cache nya blm ada conv_id nya => get detail append
          detailIds.push(payload.conversation_id);
        } else if (findConversation) {
          queryClient.setQueryData(queryKey, (old) => {
            let newData = cloneDeep(old);
            newData.data = newData.data.map((row) => {
              if (row.id === payload.conversation_id) {
                row.current_agent_id = payload.assigned_agent_id;
              }
              return row;
            });
            return newData;
          });

          if (
            shouldSupervisorStreamAssign &&
            findConversation.phone_number_id === selectedPhoneNumber?.id
          ) {
            // fokus kan ke conversation yang baru aja di assigned
            focusConversationHandler(payload.conversation_id);
          }
        }
      }

      if (!isEmpty(detailIds)) {
        const detailConversations = await fetchConversationsDetail(detailIds);
        const matched_assigned_conversation_id = [];
        detailConversations.forEach((conversation) => {
          appendToCacheConversation(conversation);
          if (conversation.phone_number_id === selectedPhoneNumber?.id) {
            matched_assigned_conversation_id.push(conversation.id);
          }
        });
        if (
          shouldSupervisorStreamAssign &&
          matched_assigned_conversation_id.length > 0
        ) {
          // fokus kan ke conversation yang baru aja di assigned
          focusConversationHandler(matched_assigned_conversation_id[0]);
        }
      }
    },
    [
      appendToCacheConversation,
      fetchConversationsDetail,
      conversationType,
      queryClient,
      user,
      queryKey,
      playNotificationSound,
      dispatch,
      currentConversation,
      focusConversationHandler,
      shouldSupervisorStreamAssign,
      selectedPhoneNumber,
    ],
  );

  const takeConversationHandler = useCallback(
    async (payload) => {
      // payload = {
      //     "conversation_id": 100,
      //     "assigned_agent_id": 1431,
      //     "data_type": "take"
      // }
      //jika yang take adalah agent yang sedang login
      if (user.id === payload.assigned_agent_id || conversationType === "all")
        return;

      //jika bukan, kita cek di cache saat ini apakah ada conversation yang di take
      const conversations = queryClient.getQueryData(queryKey);
      const findConversation = conversations?.data?.find(
        (row) => row.id === payload.conversation_id,
      );
      if (conversationType === "assigned") {
        if (
          currentConversation?.id === payload.conversation_id &&
          currentConversation?.current_agent_id === user.id
        ) {
          dispatch(setCurrentAgentId(payload.assigned_agent_id));
        }
        if (findConversation) {
          queryClient.setQueryData(queryKey, (old) => {
            let newData = cloneDeep(old);
            newData.data = newData.data.map((row) => {
              if (row.id === payload.conversation_id) {
                row.current_agent_id = payload.assigned_agent_id;
              }
              return row;
            });
            return newData;
          });
        } else {
          const detailConversations = await fetchConversationsDetail([
            payload.conversation_id,
          ]);
          detailConversations.forEach((conversation) => {
            appendToCacheConversation(conversation);
          });
        }
      } else {
        if (!findConversation) return;

        queryClient.setQueryData(queryKey, (old) => {
          let newData = cloneDeep(old);
          newData.data = newData.data.filter(
            (row) => row.id !== payload.conversation_id,
          );
          return newData;
        });

        if (currentConversation?.id === payload.conversation_id) {
          dispatch(setChat({ conversation: null, messages: [] }));
        }
      }
    },
    [
      queryClient,
      user.id,
      queryKey,
      dispatch,
      currentConversation,
      conversationType,
      appendToCacheConversation,
      fetchConversationsDetail,
    ],
  );

  const unassignedHandler = useCallback(
    async (payload) => {
      // payload = {
      //     "conversation_id": 100,
      //     "assigned_agent_id": 0,
      //     "data_type": "unassign"
      // }

      const conversations = queryClient.getQueryData(queryKey);
      const findConversation = conversations?.data?.find(
        (row) => row.id === payload.conversation_id,
      );

      if (conversationType === "all") return;

      if (conversationType === "assigned" || conversationType === "my_inbox") {
        if (!findConversation) return;

        queryClient.setQueryData(queryKey, (old) => {
          let newData = cloneDeep(old);
          newData.data = newData.data.filter(
            (row) => row.id !== payload.conversation_id,
          );
          return newData;
        });
        if (currentConversation?.id === payload.conversation_id) {
          dispatch(setChat({ conversation: null, messages: [] }));
        }
      }

      if (conversationType === "unassigned") {
        if (findConversation) return;

        const detailConversations = await fetchConversationsDetail([
          payload.conversation_id,
        ]);
        detailConversations.forEach((conversation) => {
          appendToCacheConversation(conversation);
        });
      }
      // jika ada di my inbox, hapus
      // jika ada di assigned, hapus
      // jika berada di unassigned, cek, jika blm ada tambahan
      // jika ada di all, cek jika blm ada tambah
    },
    [
      appendToCacheConversation,
      fetchConversationsDetail,
      conversationType,
      queryClient,
      queryKey,
      currentConversation,
      dispatch,
    ],
  );

  const setChatAIResponseHandler = useCallback(
    (payload) => {
      console.log(payload);
      dispatch(setChatAIResponse(payload));
    },
    [dispatch],
  );

  // Identitas tamu inhouse berubah:
  // - inhouse.guest.promoted: agent menjadikan tamu kontak (conversation ganti
  //   contact_id, id biasanya SAMA).
  // - inhouse.guest.migrated: tamu login, pesannya pindah ke conversation
  //   pelanggan (id selalu BEDA).
  // Aturan tidak bergantung nama event: id beda → old sudah DITUTUP BE (hapus),
  // id sama → cukup refresh baris dengan detail terbaru.
  const inhouseGuestIdentityHandler = useCallback(
    async (payload) => {
      // payload = {
      //   data_type: "inhouse.guest.migrated" | "inhouse.guest.promoted",
      //   old_contact_name: "Tamu #7AD3",
      //   new_contact_name: "ClientApp1 (PT Kledo)",
      //   old_conversation_id: 52,
      //   new_conversation_id: 44,
      // }
      // Fitur channel inhouse sementara khusus website internal.
      if (!isInhouseEnabled(selectedCompany?.website_id)) return;
      const oldId = payload.old_conversation_id;
      const newId = payload.new_conversation_id;
      if (!oldId || !newId) return;
      const isSameId = oldId === newId;

      // Broadcast se-company → agent yang tidak memegang conversation ini
      // (tidak ada di list & tidak sedang dibuka) tidak perlu fetch apa pun.
      const cache = queryClient.getQueryData(queryKey);
      const rows = Array.isArray(cache?.data) ? cache.data : [];
      const hasOld = rows.some((row) => row.id === oldId);
      const hasNew = rows.some((row) => row.id === newId);
      const openIdBefore = store.getState().chat.conversation?.id;
      const isOpen = openIdBefore === oldId || openIdBefore === newId;
      if (!hasOld && !hasNew && !isOpen) return;

      // Fetch dulu baru ubah cache sekaligus → tidak ada jeda list/panel kosong.
      // Gagal → fetchConversationsDetail sudah fallback refetch list.
      const [detail] = await fetchConversationsDetail([newId]);

      if (!detail) {
        // id sama: conversation masih hidup → biarkan apa adanya (lebih baik
        // nama lama sebentar daripada hilang); refetch list yang membereskan.
        if (isSameId) return;
        // id beda: old memang sudah ditutup BE → tetap hapus.
        queryClient.setQueryData(queryKey, (old) => {
          if (!old?.data || !Array.isArray(old.data)) return old;
          return { ...old, data: old.data.filter((row) => row.id !== oldId) };
        });
        dispatch(unpinConversation(oldId));
        if (store.getState().chat.conversation?.id === oldId) {
          dispatch(setChat({ conversation: null, messages: [] }));
        }
        return;
      }

      // Mode assign: new hanya boleh tampil di tab yang sesuai assignment-nya
      // (new bisa saja milik agent lain / beda status dari conversation tamu).
      let fitsTab = true;
      if (isAssignMode) {
        const assignedAgentId = detail.current_agent_id || detail.agent_id;
        if (conversationType === "my_inbox") {
          fitsTab = assignedAgentId === user.id;
        } else if (conversationType === "unassigned") {
          fitsTab = !assignedAgentId;
        } else if (conversationType === "assigned") {
          fitsTab = !!assignedAgentId;
        }
      }

      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.data || !Array.isArray(old.data)) return old;
        let data = isSameId
          ? old.data
          : old.data.filter((row) => row.id !== oldId);
        data = data.filter((row) => row.id !== newId);
        // new hanya masuk tab ini bila sesuai tab DAN menggantikan old yang
        // ada di tab ini atau memang sudah ada (di-replace dengan detail terbaru).
        if (fitsTab && (hasOld || hasNew)) {
          data.push(detail);
        }
        return { ...old, data: sortById(data) };
      });
      if (!isSameId) {
        // Pin hidup di luar query cache → bersihkan juga (lihat applyBroadcastClosed).
        dispatch(unpinConversation(oldId));
      }

      // Baca dari store hidup (bukan closure) supaya tidak salah conversation
      // bila fokus berpindah selama fetch di atas.
      const openId = store.getState().chat.conversation?.id;
      if (!isSameId && openId === oldId) {
        // old sudah ditutup → pindah ke new. Tidak sesuai tab → tutup panel,
        // konsisten dgn assign/take/unassign saat conversation keluar dari tab.
        dispatch(
          fitsTab
            ? setChat({
                conversation: omit(detail, ["messages", "counter"]),
                messages: detail.messages || [],
              })
            : setChat({ conversation: null, messages: [] }),
        );
      } else if (openId === newId) {
        // Sedang membuka new (termasuk promoted id sama): perbarui data
        // conversation (kontak baru) TANPA mengganti messages, lalu merge
        // history supaya pesan optimistic yang sedang dikirim tidak hilang.
        dispatch(
          setChat({ conversation: omit(detail, ["messages", "counter"]) }),
        );
        if (!isEmpty(detail.messages)) {
          dispatch(mergeMessages(detail.messages));
        }
      }
    },
    [
      fetchConversationsDetail,
      queryClient,
      queryKey,
      dispatch,
      isAssignMode,
      conversationType,
      user.id,
      selectedCompany?.website_id,
    ],
  );

  const eventHandler = useCallback(
    async (payload) => {
      // Safety net: error transient (mis. timeout BE) di handler manapun tidak
      // boleh jadi unhandled rejection — itu yang membuat Sentry kebanjiran.
      // Cukup log; tiap handler sudah punya recovery/retry sendiri.
      try {
        const dataType = payload.data_type;
        if (dataType === "message") {
          // BE membawa messages inline → konsumsi langsung tanpa hit endpoint.
          // Kalau tidak ada (BE lama / payload kosong) atau apply gagal → fallback fetch.
          if (Array.isArray(payload.messages) && payload.messages.length) {
            try {
              await applyBroadcastMessages(payload);
            } catch (e) {
              console.error("applyBroadcastMessages gagal, fallback fetch:", e);
              await runMessageSync();
            }
          } else {
            await runMessageSync();
          }
        } else if (dataType === "conversation_closed") {
          // BE membawa conversation_ids inline → hapus langsung tanpa fetch.
          if (
            Array.isArray(payload.conversation_ids) &&
            payload.conversation_ids.length
          ) {
            try {
              applyBroadcastClosed(payload);
            } catch (e) {
              console.error("applyBroadcastClosed gagal, fallback fetch:", e);
              await runClosedSync();
            }
          } else {
            await runClosedSync();
          }
        } else if (dataType === "status") {
          // BE membawa status_logs inline → konsumsi langsung tanpa hit endpoint.
          if (
            Array.isArray(payload.status_logs) &&
            payload.status_logs.length
          ) {
            try {
              await applyBroadcastStatus(payload);
            } catch (e) {
              console.error("applyBroadcastStatus gagal, fallback fetch:", e);
              await runStatusSync();
            }
          } else {
            await runStatusSync();
          }
        } else if (dataType === "typing") {
          await setTypingHandler(payload);
        } else if (dataType === "online") {
          await updateOnlineStatus(payload);
        } else if (dataType === "tags") {
          await setTagsHandler();
        } else if (dataType === "contact_tags") {
          await updateContactTags(payload);
        } else if (dataType === "assign") {
          await assignHandler(payload);
        } else if (dataType === "take") {
          await takeConversationHandler(payload);
        } else if (dataType === "unassign") {
          await unassignedHandler(payload);
        } else if (
          dataType === "inhouse.guest.promoted" ||
          dataType === "inhouse.guest.migrated"
        ) {
          await inhouseGuestIdentityHandler(payload);
        } else if (dataType.includes("ticket")) {
          ticketingHandler(payload);
        } else if (dataType === "chat_agent_reply") {
          setChatAIResponseHandler(payload);
        } else if (dataType === "meeting_schedule_finished") {
          meetingFinishedHandler?.(payload);
        } else if (dataType.includes("meeting_schedule_reminder")) {
          meetingReminderHandler?.(payload);
        } else if (
          [
            "meeting_schedule_created",
            "meeting_schedule_rescheduled",
            "meeting_schedule_contact_changed",
            "meeting_schedule_status_changed",
            "meeting_schedule_report_updated",
            "meeting_schedule_deleted",
          ].includes(dataType)
        ) {
          meetingScheduleNotificationHandler?.(payload);
        }
      } catch (error) {
        console.error("eventHandler error:", error);
      }
    },
    [
      applyBroadcastMessages,
      applyBroadcastStatus,
      applyBroadcastClosed,
      runMessageSync,
      runClosedSync,
      runStatusSync,
      setTypingHandler,
      updateOnlineStatus,
      setTagsHandler,
      updateContactTags,
      assignHandler,
      takeConversationHandler,
      unassignedHandler,
      inhouseGuestIdentityHandler,
      ticketingHandler,
      setChatAIResponseHandler,
      meetingReminderHandler,
      meetingFinishedHandler,
      meetingScheduleNotificationHandler,
    ],
  );

  // Baca isFetchingConversation via ref agar setBuffer (yang ter-capture di
  // closure pusher bind) selalu melihat nilai terbaru, bukan nilai render lama.
  isFetchingConvRef.current = isFetchingConversation;

  const setBuffer = useCallback((promise) => {
    if (isFetchingConvRef.current) {
      eventBuffer.current.push(promise);
    } else {
      promise();
    }
  }, []);

  useEffect(() => {
    if (!isFetchingConversation && eventBuffer.current.length > 0) {
      const bufferCopy = [...eventBuffer.current];
      eventBuffer.current = []; // Kosongkan buffer
      bufferCopy.forEach((fn) => fn());
    }
  }, [isFetchingConversation]);

  // Gate: catch-up hanya boleh jalan kalau company sudah dipilih (channelName)
  // DAN init dashboard selesai (loadedInit). Tanpa ini, koneksi Pusher yang
  // connect lebih dulu (di layar pilih perusahaan) akan memicu fetch
  // messages/statusLogs?last_id=0 → 404 berulang.
  const canSync = !!channelName && loadedInit;

  // ── Lapis 2: catch-up / rekonsiliasi ──────────────────────────────────────
  // catchUpLight: murah, hanya tarik pesan & status yang tertinggal (incremental).
  // Aman dipanggil beruntun karena runMessageSync/runStatusSync sudah single-flight
  // (coalescing), jadi tidak perlu throttle — event beruntun cukup memicu sekali.
  const catchUpLight = useCallback(() => {
    if (!canSync) return;
    runMessageSync();
    runStatusSync();
  }, [canSync, runMessageSync, runStatusSync]);

  // catchUpFull: hanya saat reconnect sungguhan. Selain catch-up incremental,
  // rekonsiliasi keanggotaan list (assign/unassign yang ikut hilang) dengan
  // refetch query AKTIF saja (background, tidak nge-blank list).
  const catchUpFull = useCallback(() => {
    if (!canSync) return;
    catchUpLight();
    queryClient
      .invalidateQueries({ queryKey, refetchType: "active" })
      .catch(() => {});
  }, [canSync, catchUpLight, queryClient, queryKey]);

  // Ref ke versi terbaru agar listener pusher/window/document bisa bind SEKALI
  // (deps stabil) tanpa rebind tiap kali handler ganti identitas.
  const catchUpLightRef = useRef(null);
  const catchUpFullRef = useRef(null);
  catchUpLightRef.current = catchUpLight;
  catchUpFullRef.current = catchUpFull;

  // Begitu cursor pesan ter-seed dari daftar conversation (0 → W), jalankan SATU
  // catch-up `messages?last_id=W` untuk menutup celah: pesan ber-id > W yang sempat
  // masuk SEBELUM Pusher subscribe (window singkat saat refresh) tidak kelewat.
  // Boolean (bukan nilai cursor) → re-render cuma sekali saat transisi 0→W, tidak
  // tiap pesan. Overlap dengan live aman (dedup-by-id + cursor monotonic).
  const isCursorSeeded = useSelector((state) => state.chat.lastMessageId > 0);
  const seededMsgCatchUpDoneRef = useRef(false);
  useEffect(() => {
    if (!canSync || seededMsgCatchUpDoneRef.current) return;
    if (isCursorSeeded) {
      seededMsgCatchUpDoneRef.current = true;
      runMessageSync();
    }
  }, [canSync, isCursorSeeded, runMessageSync]);

  // Subscribe ulang ke channel Pusher setiap kali handler relevan berubah
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel) return;

    // Satu listener per event dengan data dari Redux selector terbaru.
    // WASyncChanged membungkus data di `payload` + membawa data_type sendiri;
    // event lain (mis. inhouse.guest.*) mengirim data polos tanpa data_type →
    // pakai nama event sebagai data_type supaya bisa di-dispatch eventHandler.
    const listeners = eventNames.map((name) => {
      const listener = (data) => {
        const payload = data?.payload ?? { data_type: name, ...data };
        if (import.meta.env.DEV) {
          // Debug realtime hanya saat development (hindari noise + bocor isi chat di prod)
          // console.log("[Pusher] broadcast:", payload?.data_type, payload);
        }
        setBuffer(() => eventHandler(payload));
      };
      // Lakukan unsubscribe dari event listener yang lama
      channel.unbind(name);
      channel.bind(name, listener);
      return [name, listener];
    });

    return () => {
      listeners.forEach(([name, listener]) => channel.unbind(name, listener));
    };
    // channelName: bind ulang ke channel baru saat company berganti — channelRef
    // (object ref) tidak berubah identitas, jadi tidak memicu effect sendiri.
  }, [eventHandler, setBuffer, channelRef, channelName]);

  // Catch-up saat koneksi Pusher pulih (reconnect betulan), channel sukses
  // (re)subscribe, tab kembali aktif, dan jaringan kembali online — memulihkan
  // event yang hilang selama putus. Bind sekali per channel (pakai ref).
  useEffect(() => {
    const pusher = pusherRef.current;
    const onStateChange = (states) => {
      // Catat status koneksi untuk guard countdown auto-transfer.
      dispatch(setPusherConnected(states.current === "connected"));
      // Tandai saat koneksi lepas dari "connected" (untuk hitung durasi disconnect).
      if (states.current !== "connected" && states.previous === "connected") {
        disconnectedAtRef.current = Date.now();
      }
      if (
        states.current === "connected" &&
        states.previous !== "connected" &&
        states.previous !== "initialized"
      ) {
        // Hanya catch-up kalau putus cukup lama (bukan flicker singkat) → kurangi
        // fetch kosong saat idle. Infinity = belum sempat catat drop → tetap catch-up.
        const downMs = disconnectedAtRef.current
          ? Date.now() - disconnectedAtRef.current
          : Infinity;
        disconnectedAtRef.current = 0;
        if (downMs >= RECONNECT_CATCHUP_THRESHOLD_MS) {
          catchUpFullRef.current?.();
        }
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") catchUpLightRef.current?.();
    };
    const onOnline = () => catchUpLightRef.current?.();

    // Inisialisasi dari status saat ini (bind bisa terjadi setelah connected,
    // sehingga event "connected" pertama mungkin terlewat).
    if (pusher?.connection) {
      dispatch(setPusherConnected(pusher.connection.state === "connected"));
    }
    pusher?.connection?.bind("state_change", onStateChange);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    return () => {
      pusher?.connection?.unbind("state_change", onStateChange);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
    // channelName: re-bind ke instance pusher/channel baru saat channel berganti
  }, [pusherRef, channelRef, channelName, dispatch]);
};

export default useMessageChannel;
